import axios from 'axios'
import { AxiosInterceptor } from '../../Config/AxiosInterceptor'
import { TankType } from '../Pages/Frontend/Interface/Admin/Stations/AddStation'
// import { AddPumpForm } from '../Pages/Frontend/Interface/Admin/Stations/AddPumpModal'

AxiosInterceptor()

export type FillingStationProductType = {
  product: string
  price: number
  priceCurrency: string
  unitOfMeasure: string
  isAvailable: boolean
}
export type PumpProductType = FillingStationProductType['product']

export type PumpNozzle = 'A' | 'B'

export type FillingStationPump = {
  id: string
  fuelFactPumpSideId: string
  fuelFactFuelingPoint: string
  pumpName: string
  productType: PumpProductType
  nozzles: PumpNozzle[]
  isActive: boolean
  stationId: string
  installedAt: string
  createdAtISO8601: string
  updatedAtISO8601: string
  deletedAtISO8601: string
}

export type FillingStationAccountManager = {
  userId: string
  firstName: string
  lastName: string
  msisdn: string
  accountId: string
  photo?: { url: string }
}

export type FillingStation = {
  id: string
  businessName: string
  branchName: string
  address: string
  city: string
  state: string
  accountManagerAccountId: string
  accountManager?: FillingStationAccountManager
  latitude: number
  longitude: number
  fuelFactCompanyId: string
  fuelFactCompanyBranchId: string
  products?: FillingStationProductType[]
  fuelPumps?: FillingStationPump[]
  openingTimeHour: string
  closingTimeHour: string
  createdAtISO8601: string
  tanks: TankType[]
  stationImages?: {
    url: string
    mimetype: string
    fileCategory: string
    fileCloudProvider: string
    keyFromCloudProvider: string
  }[]
  isPublic?: boolean
}

export type ApiErrorItem = { message: string }

export type ApiResponse<T> = {
  status: boolean
  message?: string
  data?: T
  error?: string
  errors?: ApiErrorItem[]
}

export type FillingStationsListData = {
  pageNumber: number
  total: number
  pageSize: number
  dataset: FillingStation[]
}

export type ListStationsParams = {
  pageNumber: number
  id?: string
  businessName?: string
  branchName?: string
  sortOrder?: 'ASC' | 'DESC'
  accountManagerId?: string
  includeDeleted?: boolean
}

export type UpsertFillingStationPayload = {
  businessName: string
  branchName: string
  address: string
  city: string
  state: string
  latitude: number
  longitude: number
  accountManagerAccountId: string
  fuelFactCompanyId?: string
  fuelFactCompanyBranchId?: string
  openingTimeHour: string
  closingTimeHour: string
  products?: FillingStationProductType[]
  tanks?: TankType[]
  isPublic?: boolean
}

export type AddPumpPayload = {
  fuelFactPumpId: string
  pumpName: string
  productType: PumpProductType
  isActive: boolean
  nozzles: PumpNozzle[]
}

export type UpdatePumpPayload = {
  fuelFactPumpId: string
  pumpName: string
  productType: PumpProductType
  isActive: boolean
  nozzles: PumpNozzle[]
}

const fillingStationsService = {
  async list(params: ListStationsParams) {
    const queryParams = new URLSearchParams()
    queryParams.set('pageNumber', String(params.pageNumber ?? 1))
    if (params.id) queryParams.set('id', params.id)
    if (params.businessName) queryParams.set('businessName', params.businessName)
    if (params.branchName) queryParams.set('branchName', params.branchName)
    if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder)
    if (params.accountManagerId) queryParams.set('accountManagerId', params.accountManagerId)
    // if (typeof params.includeDeleted === 'boolean')
    //   queryParams.set('includeDeleted', String(params.includeDeleted))

    const res = await axios.get<ApiResponse<FillingStationsListData>>(
      `/filling-stations?${queryParams.toString()}`,
    )
    return res.data
  },

  async getById(id: string) {
    const res = await axios.get<ApiResponse<FillingStation>>(
      `/filling-stations/${encodeURIComponent(id)}`,
    )
    return res.data
  },

  async create(payload: UpsertFillingStationPayload) {
    const res = await axios.post<ApiResponse<FillingStation>>('/filling-stations', payload)
    return res.data
  },

  async update(id: string, payload: UpsertFillingStationPayload) {
    const res = await axios.put<ApiResponse<FillingStation>>(
      `/filling-stations/${encodeURIComponent(id)}`,
      payload,
    )
    return res.data
  },

  async unlinkFF(id: string) {
    const res = await axios.put<ApiResponse<FillingStation>>(
      `/filling-stations/${encodeURIComponent(id)}`,
      {
        fuelFactCompanyId: '',
        fuelFactCompanyBranchId: '',
      },
    )
    return res.data
  },

  async updateTanks(id: string, tanks: TankType[]) {
    const res = await axios.put<ApiResponse<FillingStation>>(
      `/filling-stations/${encodeURIComponent(id)}`,
      { tanks },
    )
    return res.data
  },

  async removeTank(id: string, tankIndex: number, expectedTanks: TankType[]) {
    if (tankIndex < 0 || tankIndex >= expectedTanks.length) throw new Error('Tank not found.')
    const latest = await fillingStationsService.getById(id)
    if (!latest.status || !latest.data)
      throw new Error('Could not load the latest station tanks.')
    const tanks = latest.data.tanks ?? []
    if (
      tanks.length !== expectedTanks.length ||
      tanks.some((tank, index) => {
        const expected = expectedTanks[index]
        return (
          tank.product !== expected.product ||
          tank.quantity !== expected.quantity ||
          tank.name !== expected.name
        )
      })
    ) {
      throw new Error('The tanks have changed. Refresh the station before removing a tank.')
    }
    const response = await fillingStationsService.updateTanks(
      id,
      tanks.filter((_, index) => index !== tankIndex),
    )
    if (!response.status) throw new Error(response.message || 'Could not remove the tank.')
    return response
  },

  async remove(id: string) {
    const res = await axios.delete<ApiResponse<unknown>>(
      `/filling-stations/${encodeURIComponent(id)}`,
    )
    return res.data
  },

  async addPump(stationId: string, payload: AddPumpPayload) {
    const res = await axios.post<ApiResponse<FillingStation>>(
      `/filling-stations/${encodeURIComponent(stationId)}/pumps`,
      payload,
    )
    return res.data
  },

  async updatePump(stationId: string, pumpId: string, payload: UpdatePumpPayload) {
    const res = await axios.put<ApiResponse<FillingStation>>(
      `/filling-stations/${encodeURIComponent(stationId)}/pumps/${encodeURIComponent(pumpId)}`,
      payload,
    )
    return res.data
  },

  async deletePump(stationId: string, pumpId: string) {
    const res = await axios.delete<ApiResponse<unknown>>(
      `/filling-stations/${encodeURIComponent(stationId)}/pumps/${encodeURIComponent(pumpId)}`,
    )
    return res.data
  },

  async deleteStationImage(stationId: string, keyFromCloudProvider: string) {
    const res = await axios.delete<ApiResponse<unknown>>(
      `/filling-stations/${stationId}/images?keyFromCloudProvider=${keyFromCloudProvider}`,
    )
    return res.data
  },
}

export default fillingStationsService
